import { sortBy } from 'lodash'
import { action, makeAutoObservable, values } from 'mobx'

import { copyHandbookItem, deleteHandbookItem } from 'api/clients/HandbookClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { HandbookService } from 'api/services/HandbookService'
import { HandbooksStore } from 'stores/HandbooksStore'
import { HandbookItem, HandbookItemType } from 'types'
import { HandbookTreeStore } from './HandbookTreeStore'

export class HandbookTreeItem {
  handbooksStore: HandbooksStore
  id: string
  isExpanded = false
  isChildrenLoading = false
  data: HandbookItem

  constructor(
    handbooksStore: HandbooksStore,
    item: { id: string | number; isExpanded?: boolean; isChildrenLoading?: boolean; data?: any },
  ) {
    makeAutoObservable(this)
    this.handbooksStore = handbooksStore
    this.id = item.id.toString()
    this.isChildrenLoading = item.isChildrenLoading || false
    this.data = item.data || {}
    if (item.isExpanded || this.type === HandbookItemType.Section) {
      this.expand()
    }
  }

  get type() {
    return this.data.type
  }

  get _children() {
    // Items reference their parent handbook via the `handbook.key`.
    // If a handbook item is a nested handbook, then nested_handbook.key matches its children handbook.key
    const items = values(this.handbooksStore.items).filter((item) => {
      // Legacy handbooks allow to set a parent_id pointing to another
      // HandbookItem. When this is not null, we consider them children of the
      // corresponding handbook item. Otherwise, we consider them direct
      // children of their handbook.
      if (item.data.parent_id === null) {
        return item.data.handbook.key === this.data.nested_handbook?.key
      }
      return item.data.parent_id.toString() === this.id
    })

    return sortBy(items, 'data.position')
  }

  get children() {
    return this._children.map((child) => child.id.toString())
  }

  get parentHandbook(): HandbookTreeStore | undefined {
    if (!this.data.handbook) {
      return undefined
    }
    return this.handbooksStore.get(this.data.handbook.key)
  }

  get nestedHandbook(): HandbookTreeStore | undefined {
    if (!this.data.nested_handbook) {
      return undefined
    }

    return this.handbooksStore.get(this.data.nested_handbook.key)
  }

  get parentHandbookItem(): HandbookTreeItem | undefined {
    if (!this.data.parent_id) {
      return undefined
    }
    return this.handbooksStore.items.get(this.data.parent_id.toString())
  }

  get hasChildren() {
    return this.children.length > 0
  }

  expand = () => {
    this.isExpanded = true
    if (this.type === HandbookItemType.NestedHandbook && this.data.nested_handbook) {
      this.handbooksStore.fetch(this.data.nested_handbook.key)
    }
  }

  collapse = () => {
    this.isExpanded = false
  }

  move = (fromPos: number, handbookKey: string, toPos: number = 0) => {
    const oldHandbook = this.parentHandbook
    const newHandbook = this.handbooksStore.get(handbookKey)

    if (!newHandbook || !oldHandbook) {
      return
    }

    if (oldHandbook === newHandbook) {
      // Reordering
      const items = this.parentChildren
      items.splice(toPos, 0, items.splice(fromPos, 1)[0])
      let position = 1
      items.forEach((item) => {
        item.updateAttrs({ position })
        position++
      })
      HandbookService.updateHandbookItem(Number(this.id), { position: toPos + 1 })
      return
    }

    this.updateAttrs({
      handbook: newHandbook.handbook,
      position: toPos + 1,
    })

    // Reorder all previous siblings
    oldHandbook.childrenArray
      .filter((child) => child !== this)
      .forEach((child, index) => {
        // BE data is 1-indexed, so offset by 1
        child.updateAttrs({ position: index + 1 })
      })
    // Reorder new siblings
    newHandbook.childrenArray
      .filter((child) => child !== this)
      .slice(toPos)
      .forEach((child) => {
        // Make space for the moved item by pushing the item after it by 1
        child.updateAttrs({ position: child.position + 1 })
      })

    HandbookService.updateHandbookItem(Number(this.id), {
      handbook_id: newHandbook.handbook.id,
      // BE data is 1-indexed, so offset by 1
      position: toPos + 1,
    })
  }

  delete = async (moveToParent = false) => {
    return new Promise<void>((resolve, reject) => {
      deleteHandbookItem(this.data, moveToParent)
        .then(
          action('onDeleteHandbookItem', (res) => {
            if (res.type !== SUCCESS_RESPONSE_TYPE) {
              throw new Error(res.data)
            }
            if (moveToParent) {
              this._children.forEach((child) => {
                if (this.parentHandbook) {
                  child.move(0, this.data.handbook.key, this.parentHandbook?.handbook.id)
                }
              })
            }
            this.handbooksStore.removeItem(this.id)
            if (this.parentHandbook) {
              this.handbooksStore.refresh(this.parentHandbook.handbook.key)
            }
            resolve()
          }),
        )
        .catch(reject)
    })
  }

  duplicate = () => {
    return new Promise<HandbookItem>((resolve, reject) => {
      copyHandbookItem(this.data.id)
        .then((res) => {
          if (res.type !== SUCCESS_RESPONSE_TYPE) {
            throw new Error(res.data)
          }
          resolve(res.data.handbook_item)
        })
        .catch(reject)
    })
  }

  updateAttrs = (values: Partial<HandbookItem>) => {
    this.data = { ...this.data, ...values }
  }

  get position() {
    return this.data.position
  }

  get parentChildren() {
    if (this.parentHandbookItem) {
      return this.parentHandbookItem._children
    }
    if (this.parentHandbook) {
      return this.parentHandbook.childrenArray
    }
    return []
  }

  get siblings() {
    return this.parentChildren.filter((child) => child !== this)
  }

  /**
   * Insert item at the given position. This method only deals with positions,
   * not reparenting. The item is expected to already belong to its correct
   * handbook and parent.
   */
  insertAt = (position: number) => {
    this.siblings
      .filter((sibling) => sibling.position && sibling.position >= position)
      .forEach((sibling) => {
        if (!sibling.position) {
          return
        }
        sibling.updateAttrs({ position: sibling.position + 1 })
      })
  }
}
